import { inject } from '@angular/core';
import { type CanActivateFn, type Params, Router, type UrlTree } from '@angular/router';
import type { Account } from '@frontendlib/common/types/account';
import { AccountService } from '@frontendlib/common/services/account.service';
import { chatHomeSpaceId } from '@modules/common/utils/chat-home';
import { spaceChatPath } from '@modules/common/utils/space-route';
import { catchError, map, type Observable, of, take, timeout } from 'rxjs';

/**
 * How long the resolver waits for the one identity read before it gives up and sends you to Mail.
 *
 * The same bound and the same reason as `teamSpaceBootGuard`'s: this route has no component, so a
 * read that never answers would hold a blank screen forever rather than fail.
 */
const ACCOUNT_KIND_TIMEOUT_MS = 10_000;

type ChatHomeAccount = Pick<Account, 'isPersonal' | 'homeSpaceId' | 'chatAvailable'>;

/**
 * What a short address named inside chat, carried onto the space-scoped one (#1448): the channel,
 * and the query — which is where a message address keeps its `?at=`.
 */
export interface ChatHomeDestination {
  readonly channelId?: string;
  readonly queryParams?: Params;
}

/**
 * Where chat is for this account: its own team's chat, or Mail when it has none.
 *
 * Chat is for TEAM workspaces only (Pavel, 2026-09-24), so there is nothing to choose between and
 * no picker to show: the account names its one chat, and every other outcome — a personal account,
 * an account that could not be read — is Mail, the personal default surface.
 *
 * @param refusedSpaceId a space this very navigation has just been refused. Present so an eviction
 *   can never point back at the address it came from: a team account whose home space is archived
 *   is refused by `chatSpaceMembershipGuard`, and sending it to that same home would bounce between
 *   the two guards forever.
 */
export function chatHomeUrlTree(
  account: ChatHomeAccount | null | undefined,
  router: Router,
  refusedSpaceId?: string | null,
  destination: ChatHomeDestination = {},
): UrlTree {
  const homeSpaceId = chatHomeSpaceId(account);
  return homeSpaceId && homeSpaceId !== refusedSpaceId
    ? router.createUrlTree(spaceChatPath(homeSpaceId, destination.channelId), { queryParams: destination.queryParams })
    : router.createUrlTree(['/mail']);
}

/**
 * {@link chatHomeUrlTree} against the account the app already holds.
 *
 * `getAccount()` without `force` re-uses the cached identity — `teamSpaceBootGuard` has already
 * read it before any route under `LayoutComponent` activates — so this costs no request. It never
 * completes, hence `take(1)`; it can fail, hence the timeout and the catch, and both land on Mail.
 *
 * Takes its dependencies as arguments rather than injecting them so a caller can build it once at
 * guard entry and subscribe later, outside the injection context.
 */
export function chatHomeRedirect(
  accountService: AccountService,
  router: Router,
  refusedSpaceId?: string | null,
  destination?: ChatHomeDestination,
): Observable<UrlTree> {
  return accountService.getAccount().pipe(
    take(1),
    timeout(ACCOUNT_KIND_TIMEOUT_MS),
    map((account: Account): UrlTree => chatHomeUrlTree(account, router, refusedSpaceId, destination)),
    catchError((): Observable<UrlTree> => of(router.createUrlTree(['/mail']))),
  );
}

/**
 * Whether this account has a chat at all — a team account with a home space, on a deployment that
 * runs chat.
 *
 * The same read and the same failure policy as {@link chatHomeRedirect}, so a caller can ask the
 * question without re-deriving the predicate or the 10 s bound. An account that cannot be read
 * answers `false`: chat is the thing being withheld, and withholding it is the safe direction.
 */
export function hasTeamChat(accountService: AccountService): Observable<boolean> {
  return accountService.getAccount().pipe(
    take(1),
    timeout(ACCOUNT_KIND_TIMEOUT_MS),
    map((account: Account): boolean => chatHomeSpaceId(account) !== null),
    catchError((): Observable<boolean> => of(false)),
  );
}

/**
 * `/chat` and a bare `/spaces` resolve to the account's own chat — or to Mail.
 *
 * Always a `UrlTree`: these addresses name no space, and since the picker they used to redirect to
 * is gone there is nothing for them to render. They exist so the app picker's Chat item, a
 * bookmark and a typed URL all land in the same place.
 *
 * **`/chat/:channelId` keeps its channel and its query (#1448).** It used to land on the bare home
 * chat, so a short link to a message — `/chat/<channel>?at=<seq>` — dropped both the conversation
 * and the message. Read off the route snapshot, so the routes themselves did not change.
 */
export const chatHomeGuard: CanActivateFn = (route): Observable<UrlTree> =>
  chatHomeRedirect(inject(AccountService), inject(Router), null, {
    channelId: route.paramMap.get('channelId') ?? undefined,
    queryParams: route.queryParams,
  });
